import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  Users, 
  FileText, 
  Lock,
  Heart,
  CheckCircle,
  Star,
  ArrowRight,
  Stethoscope,
  Pill,
  Activity,
  Share2,
  Smartphone,
  Clock
} from 'lucide-react';

const HealthProductLandingPage = () => {
  const features = [
    {
      icon: <Lock className="w-8 h-8" />,
      title: "Zero-Knowledge Encryption",
      description: "Your data is encrypted on your device before sending. We can't read it - only you can with your PIN."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Family Management",
      description: "Manage health records for your entire family. Parents, children, elderly parents - all in one place."
    },
    {
      icon: <Pill className="w-8 h-8" />,
      title: "Prescription Tracking",
      description: "Track medications, dosages, and refill schedules. Never miss a dose or run out of medicine."
    },
    {
      icon: <Share2 className="w-8 h-8" />,
      title: "Doctor Portal",
      description: "Share records securely with healthcare providers. Time-limited links with full audit trail."
    },
    {
      icon: <Activity className="w-8 h-8" />,
      title: "Health Diary",
      description: "Track vitals, symptoms, and health metrics over time. Spot trends and share with doctors."
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: "Works Offline",
      description: "Access your health data anywhere, even without internet. Perfect for travel and emergencies."
    }
  ];

  const whatYouCanStore = [
    { icon: <FileText />, title: "Medical History", desc: "Conditions, surgeries, hospitalizations" },
    { icon: <Pill />, title: "Prescriptions", desc: "Current & past medications with dosages" },
    { icon: <Activity />, title: "Lab Results", desc: "Blood work, imaging, pathology" },
    { icon: <Shield />, title: "Vaccinations", desc: "COVID, flu, travel vaccines" },
    { icon: <Heart />, title: "Allergies", desc: "Drug, food, environmental" },
    { icon: <Users />, title: "Family History", desc: "Genetic conditions, hereditary diseases" }
  ];

  const plans = [
    {
      name: "Monthly",
      price: "$2.99",
      period: "/month",
      features: ["Full medical history tracking", "Unlimited prescriptions", "Health diary & reminders", "Emergency QR card", "PDF export"],
      highlight: false
    },
    {
      name: "Annual",
      price: "$12.99",
      period: "/year",
      savings: "Save 64%",
      features: ["Everything in Monthly", "Aayu AI Health Assistant", "Priority support", "Advanced health insights", "Family member profiles"],
      highlight: true
    },
    {
      name: "Lifetime",
      price: "$49.99",
      period: "once",
      features: ["Everything in Annual", "One-time payment", "Forever access", "All future features", "Founding member status"],
      highlight: false
    }
  ];

  const testimonials = [
    {
      quote: "Finally, one place for our entire family's health records. The encryption gives me peace of mind that our data is truly private.",
      author: "Jennifer L.",
      role: "Mother of 3"
    },
    {
      quote: "As a caregiver for my elderly parents, this app has been invaluable. I can share their medication list with doctors instantly.",
      author: "Robert T.",
      role: "Family Caregiver"
    },
    {
      quote: "The doctor portal feature saved me 30 minutes at every specialist visit. No more filling out the same forms repeatedly.",
      author: "Amanda S.",
      role: "Chronic Illness Patient"
    }
  ];

  const securityFeatures = [
    "AES-256 Military-Grade Encryption",
    "Zero-Knowledge Architecture",
    "HIPAA Compliant",
    "SOC 2 Type II Certified",
    "GDPR Compliant",
    "No Data Selling - Ever"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-teal-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-teal-600 rounded-xl flex items-center justify-center">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl text-gray-800">EmergencyCard Health</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/pricing" className="text-gray-600 hover:text-teal-600 font-medium">
              Pricing
            </Link>
            <Link to="/login" className="text-gray-600 hover:text-teal-600 font-medium">
              Login
            </Link>
            <Link 
              to="/pricing" 
              className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2.5 rounded-full font-medium transition-all shadow-lg shadow-teal-500/25"
            >
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-4 py-20 text-center">
        <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
          <Shield className="w-4 h-4" />
          HIPAA Compliant • Zero-Knowledge Encryption
        </div>
        
        <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
          Your Family's Complete<br />
          <span className="text-teal-600">Health Passport</span>
        </h1>
        
        <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-10">
          Store your entire medical history securely. Track prescriptions, lab results, vaccinations. 
          Share instantly with doctors. 
          <strong className="text-gray-800"> Trusted by 25,000+ families.</strong>
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          <Link 
            to="/pricing"
            className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all shadow-xl shadow-teal-500/30 flex items-center gap-2"
          >
            View Plans & Pricing <ArrowRight className="w-5 h-5" />
          </Link>
          <span className="text-gray-500">7 days free • No credit card required</span>
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-teal-600" />
            HIPAA Compliant
          </div>
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-teal-600" />
            Zero-Knowledge Encryption
          </div>
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
            4.8/5 Rating
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
            Everything You Need to Manage Family Health
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            From daily prescriptions to emergency information, keep it all in one secure place.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-teal-50/50 rounded-2xl p-6 hover:shadow-lg transition-all border border-teal-100"
              >
                <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center text-teal-600 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Can Store */}
      <section className="py-20 bg-gradient-to-b from-white to-teal-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Store All Your Health Records
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whatYouCanStore.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg border border-teal-100">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center text-teal-600 mb-4">
                  {item.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">
                Your Data, Your Control
              </h2>
              <p className="text-gray-300 mb-8">
                We use zero-knowledge encryption, meaning your health data is encrypted on your device 
                before it ever reaches our servers. We literally cannot read your data - only you can 
                with your PIN.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {securityFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0" />
                    <span className="text-sm text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gray-800 rounded-3xl p-8 border border-gray-700">
              <div className="text-center mb-6">
                <Lock className="w-16 h-16 text-teal-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold">Zero-Knowledge Architecture</h3>
              </div>
              <div className="space-y-4 text-sm">
                <div className="bg-gray-700/50 rounded-xl p-4">
                  <div className="text-teal-400 font-medium mb-1">Your Device</div>
                  <div className="text-gray-400">Data encrypted with YOUR PIN</div>
                </div>
                <div className="text-center text-gray-500">↓ Encrypted</div>
                <div className="bg-gray-700/50 rounded-xl p-4">
                  <div className="text-teal-400 font-medium mb-1">Our Servers</div>
                  <div className="text-gray-400">We only see encrypted blobs</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white" id="pricing">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-gray-600 text-center mb-12">
            All plans include unlimited family members
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <div 
                key={index}
                className={`rounded-3xl p-8 ${
                  plan.highlight 
                    ? 'bg-teal-600 text-white shadow-2xl shadow-teal-500/30 scale-105' 
                    : 'bg-white border border-gray-200 shadow-lg'
                }`}
              >
                {plan.savings && (
                  <div className="inline-block bg-amber-400 text-amber-900 text-xs font-bold px-3 py-1 rounded-full mb-4">
                    {plan.savings}
                  </div>
                )}
                <h3 className={`text-xl font-semibold mb-2 ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>
                  {plan.name}
                </h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className={`text-4xl font-bold ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>
                    {plan.price}
                  </span>
                  <span className={plan.highlight ? 'text-teal-100' : 'text-gray-500'}>
                    {plan.period}
                  </span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle className={`w-5 h-5 flex-shrink-0 ${plan.highlight ? 'text-teal-200' : 'text-teal-600'}`} />
                      <span className={`text-sm ${plan.highlight ? 'text-teal-50' : 'text-gray-600'}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <Link 
                  to="/pricing"
                  className={`block w-full py-3 rounded-xl font-semibold text-center transition-all ${
                    plan.highlight 
                      ? 'bg-white text-teal-600 hover:bg-teal-50' 
                      : 'bg-teal-600 text-white hover:bg-teal-700'
                  }`}
                >
                  Choose Plan
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-b from-teal-50 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Trusted by Families Worldwide
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg border border-teal-100">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.quote}"</p>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.author}</div>
                  <div className="text-sm text-gray-500">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Take Control of Your Family's Health Today
          </h2>
          <p className="text-teal-100 text-lg mb-8 max-w-2xl mx-auto">
            Join 25,000+ families who trust EmergencyCard Health with their most important information.
          </p>
          <Link 
            to="/pricing"
            className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-teal-50 transition-all shadow-xl"
          >
            View Plans & Start Free <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-teal-200 mt-4 text-sm">
            Choose your plan • No credit card for trial • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-white">EmergencyCard Health</span>
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
              <Link to="/security" className="hover:text-white transition-colors">Security</Link>
            </div>
            <div className="text-sm">
              © 2026 EmergencyCard Health. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HealthProductLandingPage;
